"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef } from "react";

/**
 * Props for the useDragAndDrop hook
 */
interface UseDragAndDropProps {
  /** Whether drag and drop is enabled */
  enabled: boolean;
  /** Callback for when a file is dropped */
  onFileDrop: (file: File) => void;
  /** Callback to set the visual state during drag events */
  setVisualState: (state: "idle" | "dragging") => void;
}

/**
 * Interface for the return value of the useDragAndDrop hook
 */
interface DragAndDropHandlers {
  /** Reference to the drop area element */
  dropAreaRef: React.RefObject<HTMLDivElement | null>;
  /** Handler for drag enter events */
  handleDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
  /** Handler for drag over events */
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  /** Handler for drag leave events */
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  /** Handler for drop events */
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
}

/**
 * Hook for managing drag and drop interactions
 *
 * Provides event handlers and ref for implementing drag and drop functionality.
 *
 * @param props Hook props
 * @returns Object with ref and event handlers
 */
const useDragAndDrop = t0 => {
  const $ = _c(18);
  const {
    enabled,
    onFileDrop,
    setVisualState
  } = t0;
  const dropAreaRef = useRef(null);
  let t1;
  if ($[0] !== enabled || $[1] !== setVisualState) {
    t1 = e => {
      if (!enabled) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setVisualState("dragging");
    };
    $[0] = enabled;
    $[1] = setVisualState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleDragEnter = t1;
  let t2;
  if ($[3] !== enabled || $[4] !== setVisualState) {
    t2 = e_0 => {
      if (!enabled) {
        return;
      }
      e_0.preventDefault();
      e_0.stopPropagation();
      setVisualState("dragging");
    };
    $[3] = enabled;
    $[4] = setVisualState;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleDragOver = t2;
  let t3;
  if ($[6] !== enabled || $[7] !== setVisualState) {
    t3 = e_1 => {
      if (!enabled) {
        return;
      }
      e_1.preventDefault();
      e_1.stopPropagation();
      if (dropAreaRef.current && !dropAreaRef.current.contains(e_1.relatedTarget as Node)) {
        setVisualState("idle");
      }
    };
    $[6] = enabled;
    $[7] = setVisualState;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleDragLeave = t3;
  let t4;
  if ($[9] !== enabled || $[10] !== onFileDrop || $[11] !== setVisualState) {
    t4 = e_2 => {
      if (!enabled) {
        return;
      }
      e_2.preventDefault();
      e_2.stopPropagation();
      if (e_2.dataTransfer.files && e_2.dataTransfer.files.length > 0) {
        onFileDrop(e_2.dataTransfer.files[0]);
      } else {
        setVisualState("idle");
      }
    };
    $[9] = enabled;
    $[10] = onFileDrop;
    $[11] = setVisualState;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleDrop = t4;
  let t5;
  if ($[13] !== handleDragEnter || $[14] !== handleDragLeave || $[15] !== handleDragOver || $[16] !== handleDrop) {
    t5 = {
      dropAreaRef,
      handleDragEnter,
      handleDragOver,
      handleDragLeave,
      handleDrop
    };
    $[13] = handleDragEnter;
    $[14] = handleDragLeave;
    $[15] = handleDragOver;
    $[16] = handleDrop;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
export default useDragAndDrop;